/**
 * Mappa interattiva delle Commissioni regionali.
 *
 * @param {HTMLElement} wrapper Wrapper del componente.
 * @returns {void}
 */
export default function initMappaCommissioniRegionali(wrapper) {
    if (!wrapper) {
        return;
    }

    const dataScript = wrapper.querySelector("[data-commissioni-regionali-data]");
    const panel = wrapper.querySelector("[data-commissioni-panel]");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (!dataScript || !panel) {
        return;
    }

    let commissioni = {};

    try {
        commissioni = JSON.parse(dataScript.textContent || "{}");
    } catch (error) {
        console.error("Dati Commissioni regionali non validi.", error);
        return;
    }

    const viewMap = wrapper.querySelector('[data-commissioni-view="map"]');
    const viewList = wrapper.querySelector('[data-commissioni-view="list"]');
    const btnShowList = wrapper.querySelector('[data-commissioni-action="show-list"]');
    const btnShowMap = wrapper.querySelector('[data-commissioni-action="show-map"]');
    const mapRegions = Array.from(wrapper.querySelectorAll(".mappa-regioni-italia [data-commissioni-region]"));
    const listButtons = Array.from(wrapper.querySelectorAll(".commissioni-regionali-map__list [data-commissioni-region]"));

    const defaultTitle = panel.dataset.defaultTitle || "";
    const defaultDesc = panel.dataset.defaultDesc || "";
    const panelTitleId = panel.getAttribute("aria-labelledby") || "";
    let tooltip = null;

    function setView(view) {
        if (!viewMap || !viewList || !btnShowList || !btnShowMap) {
            return;
        }

        const showMap = view === "map";

        viewMap.classList.toggle("is-active", showMap);
        viewMap.hidden = !showMap;

        viewList.classList.toggle("is-active", !showMap);
        viewList.hidden = showMap;

        btnShowList.classList.toggle("d-none", !showMap);
        btnShowMap.classList.toggle("d-none", showMap);

        if (!showMap) {
            hideTooltip();
        }
    }

    function ensureTooltip() {
        if (!viewMap) {
            return null;
        }

        if (!tooltip) {
            tooltip = document.createElement("div");
            tooltip.className = "commissioni-regionali-map__tooltip";
            tooltip.setAttribute("role", "tooltip");
            tooltip.hidden = true;
            viewMap.appendChild(tooltip);
        }

        return tooltip;
    }

    function setTooltipPosition(x, y) {
        const currentTooltip = ensureTooltip();

        if (!currentTooltip || !viewMap) {
            return;
        }

        const viewRect = viewMap.getBoundingClientRect();
        const padding = 12;
        const halfTooltipWidth = currentTooltip.offsetWidth / 2;
        const tooltipHeight = currentTooltip.offsetHeight;
        const minX = halfTooltipWidth + padding;
        const maxX = viewRect.width - halfTooltipWidth - padding;
        const minY = tooltipHeight + padding;
        const maxY = viewRect.height - padding;
        const clampedX = Math.min(Math.max(x, minX), Math.max(minX, maxX));
        const clampedY = Math.min(Math.max(y, minY), Math.max(minY, maxY));

        currentTooltip.style.left = `${clampedX}px`;
        currentTooltip.style.top = `${clampedY}px`;
    }

    function showTooltip(region) {
        const currentTooltip = ensureTooltip();

        if (!currentTooltip) {
            return;
        }

        const label = region.dataset.commissioniRegionLabel || region.dataset.commissioniRegion || "";

        currentTooltip.textContent = label;
        currentTooltip.hidden = label === "";
    }

    function hideTooltip() {
        if (!tooltip) {
            return;
        }

        tooltip.hidden = true;
        tooltip.textContent = "";
        tooltip.removeAttribute("style");
    }

    function positionTooltipFromEvent(event) {
        if (!viewMap) {
            return;
        }

        const viewRect = viewMap.getBoundingClientRect();

        setTooltipPosition(event.clientX - viewRect.left, event.clientY - viewRect.top);
    }

    function positionTooltipFromRegion(region) {
        if (!viewMap) {
            return;
        }

        const viewRect = viewMap.getBoundingClientRect();
        const regionRect = region.getBoundingClientRect();

        setTooltipPosition(
            regionRect.left - viewRect.left + regionRect.width / 2,
            regionRect.top - viewRect.top + regionRect.height / 2
        );
    }

    function appendParagraph(parent, className, text) {
        if (!text) {
            return null;
        }

        const paragraph = document.createElement("p");
        paragraph.className = className;
        paragraph.textContent = text;
        parent.appendChild(paragraph);

        return paragraph;
    }

    function appendMetaRows(parent, data) {
        const rows = [
            {
                label: "Prima costituzione",
                value: data.costituzione,
            },
            {
                label: "Rinnovo",
                value: data.rinnovo,
            },
        ].filter((item) => item.value);

        if (!rows.length) {
            return;
        }

        const block = document.createElement("div");
        block.className = "commissioni-regionali-map__meta text-paragraph mb-regular";

        rows.forEach((item, index) => {
            const row = document.createElement("p");
            row.className = index === rows.length - 1 ? "mb-0" : "mb-1";

            const label = document.createElement("strong");
            label.className = "fw-title";
            label.textContent = item.label;
            row.appendChild(label);
            row.appendChild(document.createTextNode(` ${item.value}`));

            block.appendChild(row);
        });

        parent.appendChild(block);
    }

    function appendTextBlock(parent, label, value) {
        if (!value) {
            return;
        }

        const block = document.createElement("div");
        block.className = "commissioni-regionali-map__info-block";

        const title = document.createElement("h3");
        title.className = "commissioni-regionali-map__info-title text-paragraph fw-title mb-1";
        title.textContent = label;
        block.appendChild(title);

        const text = document.createElement("p");
        text.className = "commissioni-regionali-map__info-text text-paragraph mb-0";
        text.textContent = value;
        block.appendChild(text);

        parent.appendChild(block);
    }

    function appendList(parent, label, items, className = "") {
        if (!Array.isArray(items) || !items.length) {
            return;
        }

        const block = document.createElement("div");
        block.className = `commissioni-regionali-map__info-block ${className}`.trim();

        const title = document.createElement("h3");
        title.className = "commissioni-regionali-map__info-title text-paragraph fw-title mb-2";
        title.textContent = label;
        block.appendChild(title);

        const list = document.createElement("ul");
        list.className = "commissioni-regionali-map__details-list list-unstyled mb-0";

        items.forEach((item) => {
            if (!item) {
                return;
            }

            const listItem = document.createElement("li");
            listItem.textContent = item;
            list.appendChild(listItem);
        });

        block.appendChild(list);
        parent.appendChild(block);
    }

    function renderDefaultPanel() {
        panel.innerHTML = "";

        const title = document.createElement("h2");
        title.id = panelTitleId;
        title.className = "commissioni-regionali-map__title h2 typo-title fw-title color-accent-2 mb-large";
        title.textContent = defaultTitle;
        panel.appendChild(title);

        appendParagraph(
            panel,
            "commissioni-regionali-map__description text-paragraph-lg color-on-surface mb-0",
            defaultDesc
        );
    }

    function clearActiveRegions() {
        mapRegions.forEach((region) => {
            region.classList.remove("is-active");
            region.setAttribute("aria-pressed", "false");
        });

        listButtons.forEach((button) => {
            button.classList.remove("is-active");
            button.setAttribute("aria-pressed", "false");
        });
    }

    function renderCommissione(slug) {
        const data = commissioni[slug];

        if (!data) {
            renderDefaultPanel();
            return;
        }

        clearActiveRegions();

        mapRegions.forEach((region) => {
            const isCurrent = region.dataset.commissioniRegion === slug;
            region.classList.toggle("is-active", isCurrent);
            region.setAttribute("aria-pressed", isCurrent ? "true" : "false");
        });

        listButtons.forEach((button) => {
            const isCurrent = button.dataset.commissioniRegion === slug;
            button.classList.toggle("is-active", isCurrent);
            button.setAttribute("aria-pressed", isCurrent ? "true" : "false");
        });

        panel.innerHTML = "";

        const closeButton = document.createElement("button");
        closeButton.type = "button";
        closeButton.className = "commissioni-regionali-map__close";
        closeButton.setAttribute("aria-label", "Chiudi dettaglio regione");

        const closeIcon = document.createElement("span");
        closeIcon.className = "ic-close-l";
        closeIcon.setAttribute("aria-hidden", "true");
        closeButton.appendChild(closeIcon);

        closeButton.addEventListener("click", () => {
            clearActiveRegions();
            renderDefaultPanel();
        });

        panel.appendChild(closeButton);

        const title = document.createElement("h2");
        title.id = panelTitleId;
        title.className = "commissioni-regionali-map__title h3 lh-sm typo-title fw-title color-accent-2 mb-large";
        title.textContent = data.titolo || data.regione || slug;
        panel.appendChild(title);

        appendMetaRows(panel, data);

        const details = document.createElement("div");
        details.className = "commissioni-regionali-map__details";
        panel.appendChild(details);

        appendTextBlock(details, "Sede e contatti", [data.sede, data.contatti].filter(Boolean).join("\n"));
        appendTextBlock(details, "Presidente", data.presidente);
        appendTextBlock(details, "Descrizione", data.descrizione);
        appendList(details, "Aree tematiche", data.aree_tematiche, "commissioni-regionali-map__topics");
        appendList(details, "Componenti", data.componenti, "commissioni-regionali-map__members");
    }

    function getNavbarOffset() {
        let offset = 0;
        const navbar = document.getElementById("mainNavbar");

        if (navbar) {
            const navbarPosition = window.getComputedStyle(navbar).position;

            if (navbarPosition === "fixed" || navbarPosition === "sticky" || navbar.classList.contains("sticky-navbar")) {
                offset += navbar.offsetHeight;
            }
        }

        const adminBar = document.getElementById("wpadminbar");

        if (adminBar && window.getComputedStyle(adminBar).position === "fixed") {
            offset += adminBar.offsetHeight;
        }

        return offset;
    }

    function scrollToPanelMobile() {
        if (window.innerWidth >= 1025 || !panel) {
            return;
        }

        const offset = getNavbarOffset() + 12;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const top = panel.getBoundingClientRect().top + scrollY - offset;

        window.scrollTo({
            top: top < 0 ? 0 : top,
            behavior: reduceMotion.matches ? "auto" : "smooth",
        });
    }

    function activateRegion(slug) {
        if (!slug || !commissioni[slug]) {
            return;
        }

        renderCommissione(slug);
        scrollToPanelMobile();
    }

    if (btnShowList && btnShowMap) {
        btnShowList.addEventListener("click", () => setView("list"));
        btnShowMap.addEventListener("click", () => setView("map"));
    }

    mapRegions.forEach((region) => {
        const slug = region.dataset.commissioniRegion;
        const hasCommissione = Boolean(slug && commissioni[slug]);
        const label = region.dataset.commissioniRegionLabel || slug;

        region.setAttribute("aria-label", hasCommissione ? `Mostra ${label}` : `${label}: dati non disponibili`);
        region.setAttribute("aria-disabled", hasCommissione ? "false" : "true");

        if (!hasCommissione) {
            region.classList.add("is-disabled");
            region.removeAttribute("tabindex");
            return;
        }

        region.setAttribute("role", "button");
        region.setAttribute("tabindex", "0");
        region.setAttribute("aria-pressed", "false");

        region.addEventListener("mouseenter", (event) => {
            showTooltip(region);
            positionTooltipFromEvent(event);
        });
        region.addEventListener("mousemove", (event) => {
            if (!tooltip || tooltip.hidden) {
                return;
            }

            positionTooltipFromEvent(event);
        });
        region.addEventListener("mouseleave", hideTooltip);
        region.addEventListener("focus", () => {
            showTooltip(region);
            positionTooltipFromRegion(region);
        });
        region.addEventListener("blur", hideTooltip);
        region.addEventListener("click", () => activateRegion(slug));
        region.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                activateRegion(slug);
            }
        });
    });

    listButtons.forEach((button) => {
        const slug = button.dataset.commissioniRegion;

        if (!slug || !commissioni[slug]) {
            button.disabled = true;
            button.setAttribute("aria-disabled", "true");
            return;
        }

        button.setAttribute("aria-pressed", "false");
        button.addEventListener("click", () => activateRegion(slug));
    });
}
